import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { IoCheckmark } from 'react-icons/io5';
import useMusicStore from '../stores/musicStore';

// Динамическая загрузка mp3 из папки assets/music
function useMusicTracks() {
	return useMemo(() => {
		try {
			// require.context доступен в CRA (webpack)
			const ctx = require.context('../assets/music', false, /\.mp3$/);
			return ctx
				.keys()
				.filter(k => k.startsWith('./open-case_'))
				.sort()
				.map((key) => {
					const src = ctx(key);
					const filename = key.replace('./', '');
					const base = filename.replace(/\.mp3$/, '');
					const pretty = base.replace(/^open-case_\d+_/, '').replace(/_/g, ' ').trim();
					return { key: base, title: pretty, src };
				});
		} catch (_) {
			return [];
		}
	}, []);
}

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	color: ${props => props.theme.text.primary};
	display: flex;
	flex-direction: column;
	padding: 16px;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
`;

const Title = styled.h1`
	margin: 0;
	font-size: 20px;
	font-weight: 700;
`;

const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const TrackCard = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px;
	border: 1px solid ${props => props.$active ? props.theme.primary : props.theme.border};
	border-radius: 12px;
	background: ${props => props.theme.card.background};
	cursor: pointer;
	transition: border-color 0.2s, background 0.2s, opacity 0.2s;
	&:active { opacity: 0.8; }
`;

const TrackInfo = styled.div`
	display: flex;
	flex-direction: column;
`;

const TrackTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
`;

const RightIcon = styled.div`
	color: ${props => props.theme.primary};
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
`;

export default function MusicPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const audioRef = useRef(null);
	const [currentSrc, setCurrentSrc] = useState(null);
	const [currentKey, setCurrentKey] = useState(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const tracks = useMusicTracks();

	const storeIsRandom = useMusicStore(state => state.isRandom);
	const storeSelectedKey = useMusicStore(state => state.selectedKey);
	const storeSelectedSrc = useMusicStore(state => state.selectedSrc);
	const setRandomInStore = useMusicStore(state => state.setRandom);
	const setRandomSource = useMusicStore(state => state.setRandomSource);
	const selectTrackInStore = useMusicStore(state => state.selectTrack);

	// Инициализация из стора
	useEffect(() => {
		setIsPlaying(false);
		setCurrentKey(storeSelectedKey || null);
		setCurrentSrc(storeSelectedSrc || null);
	}, [storeSelectedKey, storeSelectedSrc]);

	useEffect(() => {
		if (WebApp.platform !== 'unknown') {
			WebApp.BackButton.show();
			WebApp.BackButton.onClick(() => navigate(-1));
		}
		return () => {
			if (WebApp.platform !== 'unknown') {
				WebApp.BackButton.hide();
				WebApp.BackButton.offClick();
			}
		};
	}, [navigate]);

	const playChosen = async (track) => {
		const audio = audioRef.current;
		if (!audio || !track) return;
		if (currentSrc !== track.src) {
			setCurrentSrc(track.src);
			setCurrentKey(track.key);
			requestAnimationFrame(async () => {
				audio.pause();
				audio.currentTime = 0;
				audio.load();
				await audio.play();
				setIsPlaying(true);
			});
		} else {
			audio.pause();
			audio.currentTime = 0;
			await audio.play();
			setIsPlaying(true);
		}
	};

	const handleSelectRandom = async () => {
		// Только переключаем режим, без автозапуска — дополнительно останавливаем текущее воспроизведение
		setRandomInStore(true);
		setCurrentKey(null);
		const audio = audioRef.current;
		if (audio) {
			audio.pause();
			audio.currentTime = 0;
		}
		setIsPlaying(false);
		// заранее выберем случайный src и сохраним в стор
		if (tracks.length > 0) {
			const random = tracks[Math.floor(Math.random() * tracks.length)];
			setRandomSource(random?.src || null);
		}
	};

	const handleSelectTrack = async (track) => {
		selectTrackInStore(track);
		await playChosen(track);
	};

	const handleEnded = () => {
		setIsPlaying(false);
		if (storeIsRandom) {
			// при включенном рандоме автоматически выбираем новый и обновляем стор
			if (tracks.length > 0) {
				const random = tracks[Math.floor(Math.random() * tracks.length)];
				setRandomSource(random?.src || null);
				playChosen(random);
			}
		}
	};

	return (
		<Container>
			<Header>
				<Title>{t('music.pageTitle')}</Title>
			</Header>
			<List>
				<TrackCard onClick={handleSelectRandom} $active={storeIsRandom}>
					<TrackInfo>
						<TrackTitle>Рандом</TrackTitle>
					</TrackInfo>
					{storeIsRandom && (
						<RightIcon>
							<IoCheckmark size={20} />
						</RightIcon>
					)}
				</TrackCard>

				{tracks.map(track => (
					<TrackCard key={track.key} onClick={() => handleSelectTrack(track)} $active={!storeIsRandom && storeSelectedKey === track.key}>
						<TrackInfo>
							<TrackTitle>{track.title}</TrackTitle>
						</TrackInfo>
						{!storeIsRandom && storeSelectedKey === track.key && (
							<RightIcon>
								<IoCheckmark size={20} />
							</RightIcon>
						)}
					</TrackCard>
				))}
			</List>
			<audio ref={audioRef} src={currentSrc || ''} onEnded={handleEnded} />
		</Container>
	);
}


